/* eslint-disable react/no-unstable-nested-components */
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  FatSelectItem,
  PrimitiveSelectItem,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "components/ui/Select";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import { ChevronDownIcon } from "lucide-react";
import { invariant } from "outvariant";
import React from "react";
import { useIntl } from "react-intl";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface SelectStatementTypeProps {
  disabled?: boolean;
  interim?: string | null;
  statementType?: IFinancialsStatementType;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}
export default function SelectStatementType({
  disabled = false,
  interim,
  statementType = IFinancialsStatementType.Annual,
  onChangeStatementType,
  onChangeInterim,
}: SelectStatementTypeProps) {
  const intl = useIntl();
  const theme = useTheme();

  const AVAILABLE_STATEMENT_TYPES = [
    {
      value: IFinancialsStatementType.Annual,
      label: intl.formatMessage({ defaultMessage: "Anual", id: "VmhsRa", description: "Tipo de balanço" }),
      icon: <AnnualIcon className="inline-flex rounded-sm" />,
    },
    {
      value: IFinancialsStatementType.Quarterly,
      label: intl.formatMessage({ defaultMessage: "Trimestral", id: "5UWozx", description: "Tipo de balanço" }),
      icon: <QuarterlyIcon className="inline-flex rounded-sm" />,
    },
  ];

  const AVAILABLE_INTERIM_OPTIONS = [
    {
      interim: "ALL",
      label: intl.formatMessage({
        defaultMessage: "Todos",
        id: "x7QLT2",
        description: "Opção no seletor de trimestre",
      }),
    },
    {
      interim: "Q1",
      label: intl.formatMessage({
        defaultMessage: "1° Tri",
        id: "zwuI0M",
        description: "Opção no seletor de trimestre",
      }),
    },
    {
      interim: "Q2",
      label: intl.formatMessage({
        defaultMessage: "2° Tri",
        id: "g02iyo",
        description: "Opção no seletor de trimestre",
      }),
    },
    {
      interim: "Q3",
      label: intl.formatMessage({
        defaultMessage: "3° Tri",
        id: "9MD4ye",
        description: "Opção no seletor de trimestre",
      }),
    },
    {
      interim: "Q4",
      label: intl.formatMessage({
        defaultMessage: "4° Tri",
        id: "zugwib",
        description: "Opção no seletor de trimestre",
      }),
    },
  ];

  const selectedOption = AVAILABLE_STATEMENT_TYPES.find(({ value }) => statementType === value);
  const selectedInterim = AVAILABLE_INTERIM_OPTIONS.find((candidate) => candidate.interim === interim);
  invariant(selectedOption, `Invalid selected statement type: "%s"`, statementType);

  return (
    <div className={classNames("flex w-full rounded-xl")}>
      <Select
        disabled={disabled}
        value={statementType}
        onValueChange={(value) => onChangeStatementType?.(value as IFinancialsStatementType)}
      >
        <SelectTrigger
          className={classNames(
            "inline-flex",
            "w-full min-w-28 border-none border-transparent bg-white xl:max-w-32",
            "font-aeonik text-sm font-medium tracking-wide text-slate-600",
            "hover:bg-slate-50 dark:hover:bg-slate-600",
            "ring-1 ring-inset ring-slate-200",
            "dark:ring-slate-950",
            "focus-visible:border-transparent focus-visible:ring-slate-100 focus-visible:ring-offset-slate-300",
            "disabled:select-none",
            "truncate",
            "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
            "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
            "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
            "rounded-l-xl rounded-r-none border-r-0",
            "dark:focus:ring-slate-900 dark:focus-visible:ring-slate-900",
            {
              "rounded-r-xl": !onChangeInterim,
            },
          )}
        >
          <SelectValue asChild={true}>
            <div className="pointer-events-none flex w-full items-center justify-between space-x-2">
              {selectedInterim && selectedOption.value === IFinancialsStatementType.Quarterly ? (
                <strong className={classNames("text-xs tracking-wide")}>{selectedInterim.label}</strong>
              ) : (
                <strong className={classNames("text-xs tracking-wide")}>{selectedOption.label}</strong>
              )}
            </div>
          </SelectValue>
        </SelectTrigger>

        <SelectContent
          side="bottom"
          scrollAreaViewportClassName="space-y-1"
          className={classNames("min-w-28 rounded-2xl border border-slate-300 text-sm", {
            "border-slate-900 bg-slate-700 text-slate-300 shadow-neutral-950/40": theme === "dark",
            dark: theme === "dark",
          })}
        >
          {AVAILABLE_STATEMENT_TYPES.map(({ label, value }) => {
            return (
              <PrimitiveSelectItem
                key={label}
                value={value}
                className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
              >
                <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                  <h4 className="inline-flex text-xs dark:text-slate-300">{label}</h4>
                  {/* {icon} */}
                </div>
              </PrimitiveSelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {onChangeInterim && (
        <div className="relative -ml-px block">
          <Select
            disabled={statementType !== IFinancialsStatementType.Quarterly}
            defaultValue={interim || "ALL"}
            onValueChange={(value) => {
              if (value === "ALL") {
                onChangeInterim(null);
              } else {
                onChangeInterim(value);
              }
            }}
          >
            <SelectPrimitive.Trigger
              className={classNames(
                "group inline-flex",
                "relative h-10 items-center bg-white p-2 text-slate-600",
                "rounded-r-xl",
                "hover:bg-slate-100 dark:hover:bg-slate-600",
                "ring-1 ring-inset ring-slate-200",
                "dark:ring-slate-950",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "disabled:pointer-events-none disabled:cursor-not-allowed",
                {
                  "bg-emerald-50 text-emerald-600 hover:text-slate-600":
                    interim && statementType === IFinancialsStatementType.Quarterly && theme !== "dark",
                },
              )}
            >
              <SelectValue>
                <ChevronDownIcon
                  className={classNames("-ml-px h-4 w-4 text-current opacity-50", "group-hover:opacity-100", {
                    "opacity-100": interim && statementType === IFinancialsStatementType.Quarterly,
                  })}
                  absoluteStrokeWidth={true}
                  strokeWidth={2}
                />
              </SelectValue>
            </SelectPrimitive.Trigger>

            <SelectContent
              side="top"
              sideOffset={20}
              className={classNames("rounded-xl border border-slate-200 text-sm", {
                "border-slate-900/80 bg-slate-900 text-slate-300 shadow-neutral-950/40": theme === "dark",
                dark: theme === "dark",
              })}
            >
              {AVAILABLE_INTERIM_OPTIONS.map(({ interim, label }) => {
                return (
                  <FatSelectItem key={interim} className="cursor-pointer rounded-xl text-sm" value={interim}>
                    <h4 className="py-1 text-xs text-slate-600 dark:text-slate-300">{label}</h4>
                  </FatSelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function AnnualIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} {...props}>
      <g fill="none" fillRule="nonzero">
        <path fill="#2563EB" d="M0 0h18v18H0z" />
        <g fill="#FFF">
          <path d="M7.608 5.077v7.7H5.672V8.146H4V6.573h1.089c.198 0 .35-.08.457-.242.106-.161.159-.36.159-.594v-.66h1.903ZM11.381 8.993c.337-.271.583-.515.737-.732.154-.216.231-.445.231-.687 0-.315-.09-.556-.27-.72-.18-.166-.42-.248-.72-.248a.883.883 0 0 0-.693.314c-.183.208-.275.485-.275.83v.11H8.433v-.22c0-.491.121-.937.363-1.337.242-.4.585-.716 1.029-.951.443-.235.955-.352 1.534-.352.946 0 1.674.227 2.184.682.51.455.764 1.06.764 1.815 0 .528-.125.983-.374 1.364-.25.381-.642.785-1.177 1.21l-1.408 1.111h2.992v1.595H8.499v-1.463l2.882-2.321Z" />
        </g>
      </g>
    </svg>
  );
}

function QuarterlyIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} {...props}>
      <g fill="none" fillRule="nonzero">
        <path fill="#059669" d="M0 0h18v18H0z" />
        <path
          fill="#FFF"
          d="M10.884 9.08c1.248.304 1.872.984 1.872 2.04 0 .488-.138.914-.414 1.278-.276.364-.668.646-1.176.846-.508.2-1.106.3-1.794.3-1.04 0-1.862-.264-2.466-.792C6.302 12.224 6 11.496 6 10.568h2.1c.008.424.14.758.396 1.002s.588.366.996.366c.32 0 .586-.088.798-.264.212-.176.318-.42.318-.732a.766.766 0 0 0-.318-.654c-.212-.156-.522-.234-.93-.234h-.9V8.396h.78c.384 0 .68-.084.888-.252a.833.833 0 0 0 .312-.684c0-.28-.09-.492-.27-.636-.18-.144-.418-.216-.714-.216-.336 0-.614.096-.834.288-.22.192-.338.456-.354.792H6.216c.008-.832.296-1.488.864-1.968C7.648 5.24 8.412 5 9.372 5c1.016 0 1.802.202 2.358.606.556.404.834.954.834 1.65 0 .488-.154.882-.462 1.182-.308.3-.714.514-1.218.642Z"
        />
      </g>
    </svg>
  );
}
